import { FC, useState } from "react";
import { Badge, Modal, Table } from "@mantine/core";
import { useStyle } from "./CustomTableOrgDetails.styles";
import { getKycColor } from "../../Utils/kyc";
import { capitalizeFirstLetter } from "@funded-here-interface/common/src/Utils/string";
import {
  Persona,
  RegtankReattemptApprovalEnum,
  RegtankOnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { codeToCountryNameMap } from "@funded-here-interface/common/src/constant/country";
import { PERSONA_DISPLAY_MAP } from "@funded-here-interface/common/src/constant/constant";
import OrgDetails from "../OrgDetails/OrgDetails";
import { useDisclosure } from "@mantine/hooks";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import {
  OrgDocsUploadSignedDocuments,
  RegtankRequest,
} from "../../types/org.type";
import { RoleEnum } from "@funded-here-interface/common/src/constant/enum";
import { sortRegtankRequestsByExpiry } from "../../Utils/regtankUtils";

interface IProps {
  data: any;
  refetchList: () => void;
}

const CustomTableForOrgDetails: FC<IProps> = ({ data, refetchList }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const { classes } = useStyle();
  const [id, setId] = useState<number>(0);
  const [role, setRole] = useState("");
  const [companyName, setCompanyName] = useState<string>("");

  const onClose = () => {
    close();
    refetchList();
  };

  const getBoardResoSignatureStatus = (
    orgDocsUploadSignDocs: OrgDocsUploadSignedDocuments[]
  ): boolean => {
    return orgDocsUploadSignDocs?.some((signedDocs) => {
      try {
        if (!signedDocs.info) return false;

        return JSON.parse(signedDocs.info)?.title === "board_resolution";
      } catch (error) {
        console.warn(
          "Failed to parse signedDocs.info for board resolution:",
          error
        );
        return false;
      }
    });
  };

  const getLastAgreementSignatureStatus = (
    lastFileNameToBeSigned: string,
    orgDocsUploadSignDocs: OrgDocsUploadSignedDocuments[]
  ): boolean => {
    return orgDocsUploadSignDocs?.some((signedDocs) => {
      try {
        if (!signedDocs.info) return false;

        return JSON.parse(signedDocs.info)?.title === lastFileNameToBeSigned;
      } catch (error) {
        console.warn(
          "Failed to parse signedDocs.info for last agreement:",
          error
        );
        return false;
      }
    });
  };

  const getLastFileName = (role: RoleEnum): string => {
    let lastFileNameToBeSigned = "";

    if (role === RoleEnum.merchant) {
      lastFileNameToBeSigned = "membership_agreement";
    } else if (role === RoleEnum.investor) {
      lastFileNameToBeSigned = "investor_agreement_corporate";
    } else if (role === RoleEnum.issuer) {
      lastFileNameToBeSigned = "issuer_agreement";
    }

    return lastFileNameToBeSigned;
  };

  const getSignatureStatus = (
    signDocument: OrgDocsUploadSignedDocuments[],
    userData: any[],
    orgRoleIdOfAuthorisedRepresentiative: number
  ): string => {
    const authRepRegtankRequest: RegtankRequest[] | undefined = userData.find(
      (x) => x.orgRole?.[0].id === orgRoleIdOfAuthorisedRepresentiative
    )?.regtankRequest;

    const role = userData?.[0]?.orgRole?.[0].role.name;

    let lastFileNameToBeSigned = getLastFileName(role);
    let sortedRegtankRequest: RegtankRequest[] = [];
    let result = "-";

    if (authRepRegtankRequest && authRepRegtankRequest.length > 0) {
      sortedRegtankRequest = sortRegtankRequestsByExpiry(authRepRegtankRequest);
    }

    const isBoardResoSigned = getBoardResoSignatureStatus(signDocument);
    const isAuthRepRegtankApproved = sortedRegtankRequest.some(
      (x) => x.onboardingStatus === RegtankOnboardingStatusEnum.APPROVED
    );
    const isLastAgreementSigned = getLastAgreementSignatureStatus(
      lastFileNameToBeSigned,
      signDocument
    );

    if (isBoardResoSigned && isAuthRepRegtankApproved) {
      result = isLastAgreementSigned ? "signed" : "pending";
    }
    return result;
  };

  const getSignColor = (
    signDocument: OrgDocsUploadSignedDocuments[],
    authRepRegtankRequest: any[],
    orgRoleIdOfAuthorisedRepresentiative: number
  ) => {
    const sign = getSignatureStatus(
      signDocument,
      authRepRegtankRequest,
      orgRoleIdOfAuthorisedRepresentiative
    );
    switch (sign) {
      case "signed":
        return "green";
      case "pending":
        return "orange";

      default:
        return "green";
    }
  };

  const getRegtankReattemptColor = (regtankReattemptApproval: string) => {
    switch (regtankReattemptApproval) {
      case RegtankReattemptApprovalEnum.ACTION_REQUIRED:
        return "yellow";
      case RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED:
        return "gray";
      default:
        return "gray";
    }
  };

  // TODO: handle multiple roles in one org
  const rows = data.map((row: any) => {
    // Memoize signature status to avoid triple calculation
    const signatureStatus = getSignatureStatus(
      row?.documentsUpload?.signedDocuments,
      row.user,
      row.orgRoleIdOfAuthorisedRepresentiative
    );

    return (
      <tr
        key={row.id}
        onClick={() => {
          open();
          setRole(row?.user[0]?.orgRole[0]?.role?.name);
          setId(row.id);
          setCompanyName(row?.companyName || null);
        }}
      >
        <td className={classes.td}>
          {row?.createdDate ? convertDate(row.createdDate) : "-"}
        </td>
        <td className={classes.td}>{row?.companyName || "-"}</td>
        <td className={classes.td}>
          {row?.user[0]?.orgRole[0]?.role?.name === Persona.DISTRIBUTOR
            ? codeToCountryNameMap.get(row?.countryISOCode || "") || "-"
            : row?.countryOfIncorporation || "-"}
        </td>
        <td className={classes.td}>
          {row?.user[0]?.orgRole[0]?.role?.name
            ? PERSONA_DISPLAY_MAP[row?.user[0]?.orgRole[0]?.role?.name] ||
              capitalizeFirstLetter(row?.user[0]?.orgRole[0]?.role?.name)
            : "-"}
        </td>
        <td>
          <Badge
            color={getRegtankReattemptColor(row?.regtankReattemptApproval)}
          >
            {row?.regtankReattemptApproval}
          </Badge>
        </td>
        <td>
          <Badge color={getKycColor(row?.kycStatus)}>
            {row?.kycStatus || "-"}
          </Badge>
        </td>
        <td>
          {signatureStatus !== "-" ? (
            <Badge
              color={getSignColor(
                row?.documentsUpload?.signedDocuments,
                row.user,
                row.orgRoleIdOfAuthorisedRepresentiative
              )}
            >
              {signatureStatus}
            </Badge>
          ) : (
            "-"
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className={classes.tableFlex}>
      <Table
        highlightOnHover
        withBorder
        withColumnBorders
        className={classes.csvTable}
      >
        <thead>
          <tr className={classes.tr}>
            <th className={classes.th}>
              <p className={classes.thinside}>Onboarding Date</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>Organisation Name</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>Country of Incorporation</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>Role</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>Regtank Reattempt Approval</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>KYC Status</p>
            </th>
            <th className={classes.th}>
              <p className={classes.thinside}>Signature</p>
            </th>
          </tr>
        </thead>
        <tbody style={{ cursor: "pointer" }}>{rows}</tbody>
      </Table>

      <Modal
        opened={opened}
        onClose={onClose}
        size="85%"
        withCloseButton={false}
        padding="0"
      >
        <OrgDetails
          id={id}
          role={role as Persona}
          close={onClose}
          refetchList={refetchList}
          companyName={companyName}
        />
      </Modal>
    </div>
  );
};

export default CustomTableForOrgDetails;
